import { normalizeIndianMobile } from "./caregiverCompleteProfile";

/** Current Care Manager Terms of Service / Code of Conduct version accepted at signup. */
export const CARE_MANAGER_TERMS_VERSION = "v1";

/** 8 fixed Ops-verification document types for the Care Manager onboarding vault. */
export const CM_DOCUMENT_TYPES = [
  {
    id: "AADHAAR",
    label: "Aadhaar Card",
    required: true,
    description: "Front & back copies in single PDF for KYC identification",
  },
  {
    id: "PAN",
    label: "PAN Card",
    required: true,
    description: "Income tax verification card",
  },
  {
    id: "QUALIFICATION_CERTIFICATE",
    label: "Qualification Certificate",
    required: true,
    description: "Highest medical / nursing / therapeutic degree certificate",
  },
  {
    id: "EXPERIENCE_LETTER",
    label: "Experience Letter",
    required: true,
    description: "Signed certificate from previous clinical / hospital employer",
  },
  {
    id: "POLICE_CLEARANCE_CERTIFICATE",
    label: "Police Clearance Certificate",
    required: true,
    description: "PCC certified by local state police headquarters (dated within 180 days)",
  },
  {
    id: "STATE_LICENSE",
    label: "State Nurse / Therapy License",
    required: true,
    description: "Active licensing board registry certificate",
  },
  {
    id: "PROFESSIONAL_RESUME",
    label: "Professional Resume",
    required: true,
    description: "Up-to-date CV highlighting home healthcare experiences",
  },
  {
    id: "PASSPORT",
    label: "Passport (Optional)",
    required: false,
    description: "Valid Indian passport bio page",
  },
];

export const CM_CRISIS_AVAILABILITY_OPTIONS = [
  { value: "FULL_TIME", label: "Full Time" },
  { value: "PART_TIME", label: "Part Time" },
  { value: "ON_CALL", label: "On Call" },
];

/** Preset avatar colors — rendered as inline SVG data URIs, no asset files needed. */
export const CM_PRESET_AVATAR_COLORS = [
  "#2A83D6",
  "#1F9D7C",
  "#C2793D",
  "#7C5CC4",
  "#D64F6E",
  "#4C7A3C",
];

/** Inline SVG data URI — a solid-color circle with the given initial. No network asset needed. */
export function buildPresetAvatarUrl(color, initial = "CM") {
  const label = String(initial || "CM").slice(0, 2).toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><circle cx="48" cy="48" r="48" fill="${color}"/><text x="48" y="58" font-family="Arial, sans-serif" font-size="34" font-weight="700" fill="#ffffff" text-anchor="middle">${label}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function getPresetAvatarOptions(initial = "CM") {
  return CM_PRESET_AVATAR_COLORS.map((color, idx) => ({
    id: `preset-${idx}`,
    url: buildPresetAvatarUrl(color, initial),
  }));
}

export function createEmptyCareManagerProfile() {
  return {
    basic: {
      fullName: "",
      mobile: "",
      email: "",
      city: "",
      profileImageUrl: "",
      profileImageStorageKey: "",
    },
    educationSummary: "",
    employmentHistory: [{ org: "", role: "", from: "", to: "", bullets: "", reasonForLeaving: "" }],
    certifications: [{ title: "", issuer: "", year: "" }],
    languagesSpoken: "",
    crisisAvailability: "",
    carePhilosophy: "",
    /** Read-only display unless/until a shared users.emergency_contact endpoint is wired here. */
    emergencyContact: { name: "", phone: "" },
    payrollBank: {
      bankAccountHolderName: "",
      bankName: "",
      bankIfsc: "",
      bankAccountNumber: "",
      accountNumberMasked: "",
    },
    references: [],
    documents: {},
  };
}

/** Merge a GET /care-manager/profile (or rich/bank/document response) payload into form state. */
export function mergeCareManagerProfileFromApi(data) {
  const base = createEmptyCareManagerProfile();
  if (!data) return base;

  const employmentHistory = Array.isArray(data.employmentHistory) && data.employmentHistory.length
    ? data.employmentHistory.map((job) => ({
        org: job?.org || "",
        role: job?.role || "",
        from: job?.from || "",
        to: job?.to || "",
        bullets: job?.bullets || "",
        reasonForLeaving: job?.reasonForLeaving || "",
      }))
    : base.employmentHistory;

  const certifications = Array.isArray(data.certifications) && data.certifications.length
    ? data.certifications.map((cert) => ({
        title: cert?.title || cert?.name || "",
        issuer: cert?.issuer || "",
        year: cert?.year || "",
      }))
    : base.certifications;

  const references = Array.isArray(data.professionalReferences)
    ? data.professionalReferences.slice(0, 3).map((ref) => ({
        name: ref?.name || "",
        relationship: ref?.relationship || "",
        phone: ref?.phone || "",
        email: ref?.email || "",
      }))
    : base.references;

  return {
    ...base,
    basic: {
      ...base.basic,
      ...(data.basic || {}),
      mobile: normalizeIndianMobile(data.basic?.mobile || data.mobile || ""),
      profileImageUrl: data.profileImageUrl || data.basic?.profileImageUrl || "",
      profileImageStorageKey: data.profileImageStorageKey || data.basic?.profileImageStorageKey || "",
    },
    educationSummary: data.educationSummary || "",
    employmentHistory,
    certifications,
    languagesSpoken: Array.isArray(data.languagesSpoken)
      ? data.languagesSpoken.join(", ")
      : data.languagesSpoken || "",
    crisisAvailability: data.availability || "",
    carePhilosophy: data.carePhilosophy || "",
    emergencyContact: {
      ...base.emergencyContact,
      ...(data.emergencyContact || {}),
    },
    payrollBank: {
      ...base.payrollBank,
      bankAccountHolderName: data.bankDetails?.bankAccountHolderName || "",
      bankName: data.bankDetails?.bankName || "",
      bankIfsc: data.bankDetails?.bankIfsc || "",
      // Full account number is never returned by the API — always blank until re-entered.
      bankAccountNumber: "",
      accountNumberMasked: data.bankDetails?.accountNumberMasked || "",
    },
    references,
    documents: data.documents || base.documents,
  };
}

/** Split a comma-separated languages input into a trimmed, de-duped array for the API payload. */
export function splitLanguagesInput(value) {
  return String(value || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
    .filter((v, idx, arr) => arr.indexOf(v) === idx);
}
